import type { IPublicClientApplication } from '@azure/msal-browser';
import type { AuthenticationProvider } from '@microsoft/microsoft-graph-client';
import { browserLogger } from '@newfront-insurance/dd-rum';

import { AuthOptions, type AuthState } from '../types';

export class MsalAuthenticationProvider implements AuthenticationProvider {
  private readonly pca: IPublicClientApplication;

  private readonly scopes: string[];

  private readonly redirectUri: string;

  constructor(pca: IPublicClientApplication, scopes: string[], redirectUri: string) {
    this.pca = pca;
    this.scopes = scopes;
    this.redirectUri = redirectUri;
  }

  private async acquireTokenSilent(): Promise<AuthState> {
    try {
      browserLogger.info('Trying to acquire Office token silently');

      // getAllAccounts never throws. If there was no previous interactive login, it will return an empty array
      const accounts = this.pca.getAllAccounts();

      const userAccount = await this.pca.acquireTokenSilent({
        account: accounts[0],
        scopes: this.scopes,
      });

      browserLogger.info('Acquired Office token silently');
      return {
        state: AuthOptions.LOGGED_IN,
        accessToken: userAccount.accessToken,
        userInfo: { name: userAccount.account.name, email: userAccount.account.username },
      };
    } catch (error) {
      browserLogger.info('Unable to acquire Office token silently', undefined, error as Error);
      return { state: AuthOptions.INTERACTION_REQUIRED, accessToken: null };
    }
  }

  private async acquireTokenPopup(): Promise<AuthState> {
    try {
      browserLogger.info('Trying to acquire Office token interactively');

      const userAccount = await this.pca.acquireTokenPopup({
        redirectUri: this.redirectUri,
        scopes: this.scopes,
      });

      browserLogger.info('Acquired Office token interactively.');
      return {
        state: AuthOptions.LOGGED_IN,
        accessToken: userAccount.accessToken,
        userInfo: { name: userAccount.account.name, email: userAccount.account.username },
      };
    } catch (error) {
      browserLogger.error('Unable to acquire Office token interactively', undefined, error as Error);
      return { state: AuthOptions.ERROR, accessToken: null, error: error as Error };
    }
  }

  public async login(): Promise<AuthState> {
    let authResult = await this.acquireTokenSilent();

    if (authResult.state === AuthOptions.INTERACTION_REQUIRED) {
      authResult = await this.acquireTokenPopup();
    }

    return authResult;
  }

  async getAccessToken(): Promise<string> {
    const authResult = await this.login();

    if (authResult.accessToken) {
      return authResult.accessToken;
    }

    let errorMessage = '';
    if (authResult.error) {
      errorMessage = `Unable to sign-in to Office: ${authResult.error instanceof Error ? authResult.error.message : authResult.error}`;
    } else {
      errorMessage = 'Unable to sign-in to Office: Authentication state is not LOGGED_IN';
    }

    browserLogger.error('[MS Graph Auth] Failed to get access token', {
      authState: authResult.state,
      hasError: !!authResult.error,
      errorMessage,
    });

    throw new Error(errorMessage);
  }
}

export { AuthOptions };
